import {Theme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useState} from 'react';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import {OPERATOR, WhereConstraints} from '../../../graphql/common.datatypes';
import getPayments from '../getPayments.api';
import Results from './Results';
import {Payment, PaymentType} from '../../../graphql/payments.datatypes';
import SearchFilter, {FilterValues, initialFilters} from './SearchFilter';
import PaymentEditModal from '../PaymentEditModal';
import getPaymentsInMyGroup from '../getPaymentsInMyGroup.api';
import getPaymentsAndGroupMemberPayments from "../getPaymentsAndGroupMemberPayments.api";
import getTransfersInMyGroup from "../getTransfersInMyGroup.api";

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    results: {
        marginTop: theme.spacing(3)
    }
}));

export const PaymentsListContent = (props: { billingPartyId?: number, billingPartyMemberId?: number, canEdit: boolean, isAdmin: boolean, groupRef?: string }) => {
    const {billingPartyId, billingPartyMemberId, canEdit, isAdmin, groupRef} = props;
    const classes = useStyles();
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [page, setPage] = useState(1);
    const [orderBy] = useState([{column: 'timestamp', order: 'DESC'}]);
    const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
    const hasBillingPartyMember = billingPartyId ? {
        column: 'BILLING_PARTY_ID',
        operator: OPERATOR.EQ,
        value: billingPartyId
    } : undefined;

    const addWhereBillingPartyMemberId = (constraints: WhereConstraints) => {
        const whereConstraints = {...constraints}
        if (billingPartyMemberId) {
            whereConstraints.AND = [
                {column: 'billing_party_member_id', operator: OPERATOR.EQ, value: billingPartyMemberId},
            ]
        }
        return whereConstraints;
    }

    const [filters, setFilters] = useState(initialFilters);

    const applyFilters = (newFilters: FilterValues) => {
        setFilters(newFilters);
        setWhere(getConstraintsFromFilters(newFilters));
    };

    const getConstraintsFromFilters = (newFilters: FilterValues) => {
        let whereConstraints: WhereConstraints = {
            AND: [],
        };

        if (newFilters.start_date) whereConstraints.AND!.push({
            column: 'timestamp',
            operator: OPERATOR.GTE,
            value: newFilters.start_date
        });
        if (newFilters.end_date) whereConstraints.AND!.push({
            column: 'timestamp',
            operator: OPERATOR.LTE,
            value: newFilters.end_date
        });
        if (newFilters.low_amount) whereConstraints.AND!.push({
            column: 'amount',
            operator: OPERATOR.GTE,
            value: newFilters.low_amount
        });
        if (newFilters.high_amount) whereConstraints.AND!.push({
            column: 'amount',
            operator: OPERATOR.LTE,
            value: newFilters.high_amount
        });
        if (newFilters.payment_types && newFilters.payment_types.length > 0) whereConstraints.AND!.push({
            column: 'payment_type',
            operator: OPERATOR.IN,
            value: newFilters.payment_types
        });
        if (newFilters.payment_status && newFilters.payment_status.length > 0) whereConstraints.AND!.push({
            column: 'payment_status',
            operator: OPERATOR.IN,
            value: newFilters.payment_status
        });

        whereConstraints = addWhereBillingPartyMemberId(whereConstraints)
        return whereConstraints;
    };

    const [where, setWhere] = useState(getConstraintsFromFilters(initialFilters));
    const paymentsData =
        isAdmin && billingPartyId
            ? getPaymentsAndGroupMemberPayments(rowsPerPage, page, billingPartyId, PaymentType.TRANSFER)
            : !isAdmin && billingPartyId
                ? getPaymentsInMyGroup(groupRef!, billingPartyId, rowsPerPage, page, where, orderBy, hasBillingPartyMember)
                : getPayments(rowsPerPage, page, where, orderBy, hasBillingPartyMember);

    // Fetch incoming transfers for non-admin users (transfers FROM other billing parties TO this one)
    const incomingTransfersData = !isAdmin && billingPartyId && groupRef
        ? getTransfersInMyGroup(groupRef, billingPartyId, rowsPerPage, page)
        : { data: null, loading: false };

    let payments;
    if (isAdmin && paymentsData.data && paymentsData.data.paymentsWithGroupMemberPaymentsOfType) {
        payments = paymentsData.data.paymentsWithGroupMemberPaymentsOfType
    } else if (!isAdmin && paymentsData.data && paymentsData.data.paymentsInMyGroup) {
        payments = paymentsData.data.paymentsInMyGroup
    } else {
        payments = paymentsData.data && paymentsData.data.payments
    }

    // Get incoming transfers for non-admin users
    const incomingTransfers = !isAdmin && incomingTransfersData.data && incomingTransfersData.data.transfersInMyGroup
        ? incomingTransfersData.data.transfersInMyGroup.data
        : [];

    return (
        <React.Fragment>
            {isAdmin && (
                <SearchBar filterContent={
                    <SearchFilter filters={filters} initialFilters={initialFilters} applyFilters={applyFilters}/>
                }/>
            )}
            <LoadingOverlay loading={paymentsData.loading}>

                {payments && (

                    <Results
                        className={classes.results}
                        payments={payments.data}
                        paginatorInfo={payments.paginatorInfo}
                        setSelectedPayment={setSelectedPayment}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        isAdmin={isAdmin}
                        billingPartyId={billingPartyId}
                        incomingTransfers={incomingTransfers}
                    />
                )}
            </LoadingOverlay>

            {isAdmin && selectedPayment && (
                <PaymentEditModal onClose={() => setSelectedPayment(null)}
                                  open={true} payment={selectedPayment} setSelectedPayment={setSelectedPayment}
                                  isAdmin={isAdmin} canEdit={canEdit}/>
            )}

        </React.Fragment>
    );
};

const PaymentsList = () => {
    const classes = useStyles();
    const title = 'Payments';

    return (
        <Page className={classes.root} title={title}>
            <PageHeader section="payments" title={title}/>
            <PaymentsListContent isAdmin={true} canEdit={true}/>
        </Page>
    );
};

export default PaymentsList;
